import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, finalize, map, tap } from 'rxjs/operators';

import { of } from 'rxjs';
import {
    fetchRecords,
    fetchRecordsSuccess,
    fetchRecordsFail,
    addRecord,
    addRecordSuccess,
    addRecordFailure,
    updateRecord,
    updateRecordSuccess,
    updateRecordFailure,
    deleteRecord,
    deleteRecordSuccess,
    deleteRecordFailure,
    fetchSingleRecord,
    fetchSingleRecordSuccess,
    fetchSingleRecordFail,
    searchClubs,
    searchClubsSuccess,
    searchClubsFail,
    vatCheck,
    vatCheckSuccess,
    vatCheckFail,
    searchCompanyProfile,
    searchCompanyProfileSuccess,
    searchCompanyProfileFail,
    importRecord,
    fetchClubMembersSuccess,
    fetchClubMembers,
    fetchClubMembersFail,
    updateClubInstallment,
    updateClubInstallmentSuccess,
    updateClubInstallmentFailure,
    importRecordSuccess,
    importRecordFailure,
    disconnectPayment,
    disconnectPaymentSuccess,
    disconnectPaymentFailure,
    getPayment,
    getPaymentSuccess,
    getPaymentFailure,
    archiveRecordFailure,
    archiveRecordSuccess,
    archiveRecord,
    getCollectMaster,
    getCollectMasterSuccess,
    getCollectMasterFailure,
    connectCollectMaster,
    connectCollectMasterSuccess,
    disconnectCollectMaster,
    connectCollectMasterFailure,
    disconnectCollectMasterSuccess,
    disconnectCollectMasterFailure
} from './clubs.action';


import { AlertService } from 'src/app/core/services/alert.service';
import { CrudServerService } from 'src/app/core/services/crud-server.service';
import { RouterService } from 'src/app/core/services/router.service';
import { Store } from '@ngrx/store';
import { RootReducerState } from '..';
import { ClubsTableService } from './clubs-table.service';
import { DatatableService } from 'src/app/core/services/datatable.service';
import { HelperService } from 'src/app/core/services/helper.service';


@Injectable()
export class ClubsRecordEffects {

    fetchData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchRecords),
            exhaustMap(() => {
                const url = 'clubs';
                return this.crudServerService.fetchTableData(url, this.moduleTableService).pipe(
                    map( (response) => {
                        const data = response.data;
                        const total = data.pagination.total;

                        this.moduleTableService.page = data.pagination.current_page;
                        this.moduleTableService.lastPage = data.pagination.last_page;
                        this.moduleTableService.pagination = this.tableService.getPaginationArray(data.pagination.current_page, data.pagination.last_page)
                        this.moduleTableService.categories = data.categories;


                        this.alertService.triggerAlert(response.message, "success");
                        return fetchRecordsSuccess({listData: data.records, total: total, isVatValid: false});
                    }),
                    
                    catchError((error) =>
                        of(error).pipe(
                            tap(() => {
                                this.alertService.triggerAlert(error, "error");
                            }),
                            map(() => fetchRecordsFail({ error })) // Dispatch 
                        )
                    )
                )
            })
        )
    );

    addData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(addRecord),
            exhaustMap((data: any) => {
                const url = 'clubs';
                return this.crudServerService.addData(url, data.newData).pipe(
                    map( (response) => {
                        this.alertService.triggerAlert(response.message, "success");
                        this.routeService.triggerRoute(response, "/clubs");
                        return addRecordSuccess();
                    }),
                    
                    catchError((error) =>
                        of(error).pipe(
                            tap(() => {
                                this.alertService.triggerAlert(error, "error");
                            }),
                            map(() => addRecordFailure({ error })) // Dispatch 
                        )
                    ),

                    finalize(() => {
                        this.helperService.formButtonDisabled = false;
                    })
                )
            })
        )
    );

    importRecord$ = createEffect(() =>
            this.actions$.pipe(
                ofType(importRecord),
                exhaustMap((data: any) => {
                    const url = 'import-club';
                    return this.crudServerService.addData(url, data.newData).pipe(
                        map( (response) => {
                            const data = response.data;
                            if(data.failedRows){
                                this.alertService.triggerAlert("ALERTS.rows-not-imported | "+data.failedRows, "warning");
                            }
    
                            this.alertService.triggerAlert(response.message, "success");
                            this.routeService.triggerRoute(response, "/clubs");
                            return importRecordSuccess();
                        }),
                        
                        catchError((error) =>
                            of(error).pipe(
                                tap(() => {
                                    this.alertService.triggerAlert(error, "error");
                                }),
                                map(() => importRecordFailure({ error })) // Dispatch 
                            )
                        )
                    )
                })
            )
    );
    
    fetchSingle$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchSingleRecord),
            exhaustMap((data: any) => {
                const url = 'clubs/'+data.id+"/edit?gather_stats="+(data.gather_stats ?? false);
                return this.crudServerService.fetchData(url,).pipe(
                    map( (response) => {

                        const data = response.data;

                        const club = data.club;
                        this.store.dispatch(searchClubsSuccess({clubItems: data.tags, clubSearch: club?.club_api_name}));
                        this.alertService.triggerAlert(response.message, "success");
                        if(club)
                            club.overviewData = data.details;

                        return fetchSingleRecordSuccess({singleData: club, countryList: data.countries, categories: data.categories});
                    }),
                    
                    catchError((error) =>
                        of(error).pipe(
                            tap(() => {
                                this.alertService.triggerAlert(error, "error");
                                this.routeService.triggerRoute(null, "/clubs");
                            }),
                            map(() => fetchSingleRecordFail({ error })) // Dispatch 
                        )
                    )
                )
            })
        )
    );

    updateData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateRecord),
            exhaustMap((data:any) => {
                const url = 'club-update/'+data.id;
                return this.crudServerService.updateDataPost(url, data.updatedData).pipe(
                    map( (response) => {
                        this.alertService.triggerAlert(response.message, "success");
                        this.routeService.triggerRoute(null, ("/clubs/detail/"+data.id));
                        return updateRecordSuccess();
                    }),
                    
                    catchError((error) =>
                        of(error).pipe(
                            tap(() => {
                                this.alertService.triggerAlert(error, "error");
                            }),
                            map(() => updateRecordFailure({ error })) // Dispatch 
                        )
                    ),

                    finalize(() => {
                        this.helperService.formButtonDisabled = false;
                    })
                )
            })
        )
    );

    updateClubInstallment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateClubInstallment),
            exhaustMap((data:any) => {
                const url = 'update-club-installments/'+data.id;
                return this.crudServerService.updateDataPost(url, data.updatedData).pipe(
                    map( (response) => {
                        this.alertService.triggerAlert(response.message, "success");
                        return updateClubInstallmentSuccess();
                    }),
                    
                    catchError((error) =>
                        of(error).pipe(
                            tap(() => {
                                this.alertService.triggerAlert(error, "error");
                            }),
                            map(() => updateClubInstallmentFailure({ error })) // Dispatch 
                        )
                    ),

                    finalize(() => {
                        this.helperService.formButtonDisabled = false;
                    })
                )
            })
        )
    );

    archiveRecord$ = createEffect(() =>
        this.actions$.pipe(
            ofType(archiveRecord),
            exhaustMap((data: any) => {
                const url = 'archive-club/'+data.id;
                return this.crudServerService.fetchData(url).pipe(
                    map( (response) => {
                        this.alertService.triggerAlert(response.message, "success");
                        this.store.dispatch(fetchRecords());
                        this.routeService.triggerRoute(null, "/clubs");
                        return archiveRecordSuccess();
                    }),
                    
                    catchError((error) =>
                        of(error).pipe(
                            tap(() => {
                                this.alertService.triggerAlert(error, "error");
                            }),
                            map(() => archiveRecordFailure({ error })) // Dispatch 
                        )
                    )
                )
            })
        )
    );

    deleteData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(deleteRecord),
            exhaustMap((data: any) => {
                const url = 'clubs/'+data.id;
                return this.crudServerService.deleteData(url).pipe(
                    map( (response) => {
                        this.alertService.triggerAlert(response.message, "success");
                        this.store.dispatch(fetchRecords());
                        this.routeService.triggerRoute(null, "/clubs");
                        return deleteRecordSuccess();
                    }),
                    
                    catchError((error) =>
                        of(error).pipe(
                            tap(() => {
                                this.alertService.triggerAlert(error, "error");
                            }),
                            map(() => deleteRecordFailure({ error })) // Dispatch 
                        )
                    )
                )
            })
        )
    );

    searchClubs$ = createEffect(() =>
        this.actions$.pipe(
            ofType(searchClubs),
            exhaustMap((data: any) => {
                const url = 'search-clubs?search='+data.text;
                return this.crudServerService.fetchData(url).pipe(
                    map( (response) => {
                        this.alertService.triggerAlert(response.message, "success");
                        return searchClubsSuccess({clubItems: response.data.list, clubSearch: data.text});
                    }),
                    
                    catchError((error) =>
                        of(error).pipe(
                            tap(() => {
                                this.alertService.triggerAlert(error, "error");
                            }),
                            map(() => searchClubsFail({ error })) // Dispatch 
                        )
                    )
                )
            })
        )
    );

    searchCompanyProfile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(searchCompanyProfile),
            exhaustMap((data: any) => {
                const url = 'company-coc-profile/'+data.kvknumber;
                return this.crudServerService.fetchData(url).pipe(
                    map( (response) => {
                        return searchCompanyProfileSuccess({companyData: response.data?.address});
                    }),
                    
                    catchError((error) =>
                        of(error).pipe(
                            tap(() => {
                                // this.alertService.triggerAlert(error, "error");
                            }),
                            map(() => searchCompanyProfileFail({ error })) // Dispatch 
                        )
                    )
                )
            })
        )
    );

    vatCheck$ = createEffect(() =>
        this.actions$.pipe(
            ofType(vatCheck),
            exhaustMap((data: any) => {
                const url = 'check-vat/'+data.text;
                return this.crudServerService.fetchData(url).pipe(
                    map( (response) => {
                        this.alertService.triggerAlert(response.message, "success");
                        return vatCheckSuccess({isVatValid: response.data.isVatValid, vatValue: data.text});
                    }),
                    
                    catchError((error) =>
                        of(error).pipe(
                            tap(() => {
                                this.alertService.triggerAlert(error, "error");
                            }),
                            map(() => vatCheckFail({ error })) // Dispatch 
                        )
                    )
                )
            })
        )
    );

    fetchClubMembers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchClubMembers),
            exhaustMap((data: any) => {
                const url = 'club-members/'+data.clubId;
                return this.crudServerService.fetchTableData(url, this.moduleTableService).pipe(
                    map( (response) => {
                        const data = response.data;
                        this.alertService.triggerAlert(response.message, "success");
                        return fetchClubMembersSuccess({clubMembers: data.members});
                    }),
                    
                    catchError((error) =>
                        of(error).pipe(
                            tap(() => {
                                this.alertService.triggerAlert(error, "error");
                            }),
                            map(() => fetchClubMembersFail({ error })) // Dispatch 
                        )
                    )
                )
            })
        )
    );

    getPayment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getPayment),
            exhaustMap((data: any) => {

                const url = 'mollie/status/'+data.clubId;
                return this.crudServerService.fetchData(url).pipe(
                    map( (response) => {
                        return getPaymentSuccess({hasConnect: response.data.hasConnect});
                    }),
                    
                    catchError((error) =>
                        of(error).pipe(
                            tap(() => {
                                this.alertService.triggerAlert(error, "error");
                            }),
                            map(() => getPaymentFailure({ error })) // Dispatch 
                        )
                    )
                )
            })
        )
    );

    disconnectPayment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(disconnectPayment),
            exhaustMap((data: any) => {
                const url = 'mollie/disconnect/'+data.clubId;
                return this.crudServerService.fetchData(url).pipe(
                    map( (response) => {
                        this.alertService.triggerAlert(response.message, "success");
                        this.store.dispatch(getPaymentSuccess({hasConnect: response.data.hasConnect}));
                        return disconnectPaymentSuccess();
                    }),
                    
                    catchError((error) =>
                        of(error).pipe(
                            tap(() => {
                                this.alertService.triggerAlert(error, "error");
                            }),
                            map(() => disconnectPaymentFailure({ error })) // Dispatch 
                        )
                    )
                )
            })
        )
    );

    getCollectMaster$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getCollectMaster),
            exhaustMap((data: any) => {
                const url = 'collect-master/status/'+data.clubId;
                return this.crudServerService.fetchData(url).pipe(
                    map( (response) => {
                        return getCollectMasterSuccess({hasCollectMaster: response.data.hasCollectMaster});
                    }),
                    
                    catchError((error) =>
                        of(error).pipe(
                            tap(() => {
                                this.alertService.triggerAlert(error, "error");
                            }),
                            map(() => getCollectMasterFailure({ error })) // Dispatch 
                        )
                    )
                )
            })
        )
    );

    connectCollectMaster$ = createEffect(() =>
        this.actions$.pipe(
            ofType(connectCollectMaster),
            exhaustMap((data: any) => {
                const url = 'collect-master/connect/'+data.clubId;
                return this.crudServerService.fetchData(url).pipe(
                    map( (response) => {
                        this.alertService.triggerAlert(response.message, "success");
                        this.store.dispatch(getCollectMaster({clubId: data.clubId}));
                        return connectCollectMasterSuccess();
                    }),
                    
                    catchError((error) =>
                        of(error).pipe(
                            tap(() => {
                                this.alertService.triggerAlert(error, "error");
                            }),
                            map(() => connectCollectMasterFailure({ error })) // Dispatch 
                        )
                    )
                )
            })
        )
    );

    disconnectCollectMaster$ = createEffect(() =>
        this.actions$.pipe(
            ofType(disconnectCollectMaster),
            exhaustMap((data: any) => {
                const url = 'collect-master/disconnect/'+data.clubId;
                return this.crudServerService.fetchData(url).pipe(
                    map( (response) => {
                        this.alertService.triggerAlert(response.message, "success");
                        this.store.dispatch(getCollectMaster({clubId: data.clubId}));
                        return disconnectCollectMasterSuccess();
                    }),
                    
                    catchError((error) =>
                        of(error).pipe(
                            tap(() => {
                                this.alertService.triggerAlert(error, "error");
                            }),
                            map(() => disconnectCollectMasterFailure({ error })) // Dispatch 
                        )
                    )
                )
            })
        )
    );
    
    constructor(
        private actions$: Actions,
        private crudServerService: CrudServerService,
        private alertService: AlertService,
        private routeService: RouterService,
        private store: Store<RootReducerState>,
        private moduleTableService: ClubsTableService,
        private tableService: DatatableService,
        public helperService: HelperService
    ) { }

}